import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import ServiceWorkerUpdater from "@/components/ServiceWorkerUpdater";
import OfflineBanner from "@/components/OfflineBanner";
import InstallNudge from "@/components/InstallNudge";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import DomainDetail from "@/pages/DomainDetail";
import Compare from "@/pages/Compare";
import Changes from "@/pages/Changes";
import Pricing from "@/pages/Pricing";
import Subscription from "@/pages/Subscription";
import Admin from "@/pages/Admin";
import Leads from "@/pages/Leads";
import WhyEmergent from "@/pages/WhyEmergent";
import BackendScan from "@/pages/BackendScan";
import Migration from "@/pages/Migration";
import Landing from "@/pages/Landing";
import CommandCenter from "@/pages/CommandCenter";
import Integrations from "@/pages/Integrations";
import WorkspaceSettings from "@/pages/WorkspaceSettings";
import Automations from "@/pages/Automations";
import ShareView from "@/pages/ShareView";
import Journey from "@/pages/Journey";
import HealthcareCheck from "@/pages/HealthcareCheck";
import Branding from "@/pages/Branding";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

function getSessionId() {
  let sid = localStorage.getItem("lwp_sid");
  if (!sid) {
    sid = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());
    localStorage.setItem("lwp_sid", sid);
  }
  return sid;
}

function usePageTracking() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash?.includes("session_id=")) return;
    api.post("/track", {
      path: location.pathname,
      referrer: document.referrer || null,
      session_id: getSessionId(),
    }).catch(() => {});
  }, [location.pathname]);
}

function AdminOnly({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Loader2 className="animate-spin text-[#007AFF]" />
      </div>
    );
  }
  return isAdmin ? children : <Navigate to="/dashboard" replace />;
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Loader2 className="animate-spin text-[#007AFF]" />
      </div>
    );
  }
  return user ? <Navigate to="/command" replace /> : <Landing />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Loader2 className="animate-spin text-[#007AFF]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function useFreshOnResume() {
  useEffect(() => {
    // Reload when restored from bfcache (common on iOS standalone/home-screen apps)
    const onPageShow = (e) => { if (e.persisted) window.location.reload(); };
    // Reload when the app is reopened after being backgrounded for a while
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt > 60000) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}

function AppRouter() {
  const location = useLocation();
  usePageTracking();
  useFreshOnResume();
  // Handle OAuth callback synchronously before any protected route runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<Login />} />
      <Route path="/share/:token" element={<ShareView />} />
      <Route path="/hipaa-check" element={<HealthcareCheck />} />
      <Route path="/why-emergent" element={<WhyEmergent />} />
      <Route path="/command" element={<Protected><CommandCenter /></Protected>} />
      <Route path="/journey" element={<Protected><Journey /></Protected>} />
      <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
      <Route path="/automations" element={<Protected><Automations /></Protected>} />
      <Route path="/workspace/settings" element={<Protected><WorkspaceSettings /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/domain/:domain" element={<Protected><DomainDetail /></Protected>} />
      <Route path="/compare" element={<Protected><Compare /></Protected>} />
      <Route path="/changes" element={<Protected><Changes /></Protected>} />
      <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
      <Route path="/subscription" element={<Protected><Subscription /></Protected>} />
      <Route path="/branding" element={<Protected><Branding /></Protected>} />
      <Route path="/backend-scan" element={<Protected><BackendScan /></Protected>} />
      <Route path="/migration" element={<Protected><Migration /></Protected>} />
      <Route path="/admin" element={<Protected><AdminOnly><Admin /></AdminOnly></Protected>} />
      <Route path="/admin/leads" element={<Protected><AdminOnly><Leads /></AdminOnly></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <WorkspaceProvider>
          <Toaster theme="dark" position="top-right" richColors />
          <ServiceWorkerUpdater />
          <OfflineBanner />
          <InstallNudge />
          <AppRouter />
        </WorkspaceProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
